import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Custom navigation component for SPVintage.
 * Manages announcement bar, mobile drawer with multi-panel navigation,
 * and desktop mega-menu hover dropdowns.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} drawer - The mobile drawer container
 * @property {HTMLElement} overlay - The backdrop overlay
 * @property {HTMLElement} panelMain - Step 1 main menu panel
 * @property {HTMLElement[]} panelSub - Step 2 sub-panels (one per parent with children)
 * @property {HTMLElement[]} megamenu - Desktop mega-menu dropdown panels
 *
 * @extends {Component<Refs>}
 */
class SpvNavigation extends Component {
  requiredRefs = ['drawer', 'overlay', 'panelMain'];

  /** @type {HTMLElement|null} */
  #activeSubPanel = null;

  /** @type {Element|null} */
  #activeSubPanelTrigger = null;

  /** @type {HTMLElement|null} */
  #activeMegaMenu = null;

  /** @type {HTMLElement|null} */
  #activeMegaNavItem = null;

  /** @type {number|null} */
  #megaLeaveTimeout = null;

  /** @type {ResizeObserver|null} */
  #headerResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    this.#observeHeaderHeight();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#headerResizeObserver?.disconnect();
    this.#headerResizeObserver = null;
  }

  // ─── Sticky header height → CSS var ───
  // Stock header.liquid's JS publishes --header-height on <body>, but this
  // theme renders spv-navigation instead, so that script bails (it looks for
  // <header-component>). Publish it here so sticky content below the header
  // (the product gallery + info column) can offset itself with
  // calc(var(--header-height) + …). The whole .spv-navigation-section is the
  // sticky band (announcement bar + nav row), so we report its full height.

  #observeHeaderHeight() {
    const target = this.closest('.spv-navigation-section') ?? this;
    this.#updateHeaderHeight();
    this.#headerResizeObserver = new ResizeObserver(() => this.#updateHeaderHeight());
    this.#headerResizeObserver.observe(target);
  }

  #updateHeaderHeight() {
    const section = this.closest('.spv-navigation-section') ?? this;
    // Only a sticky header occupies a fixed band at the top; when sticky is
    // off it scrolls away and should reserve no space.
    const sticky = this.dataset.sticky === 'true';
    const height = sticky ? Math.round(section.getBoundingClientRect().height) : 0;
    document.body.style.setProperty('--header-height', `${height}px`);
  }

  // ─── Drawer ───

  openDrawer() {
    const { drawer, overlay } = this.refs;
    drawer.setAttribute('aria-hidden', 'false');
    overlay.setAttribute('data-visible', 'true');
    document.documentElement.classList.add('spv-scroll-lock');

    onAnimationEnd(drawer, () => trapFocus(drawer), { subtree: false });
  }

  closeDrawer() {
    const { drawer, overlay } = this.refs;

    // Close any open sub-panel first
    if (this.#activeSubPanel) {
      this.#closeActiveSubPanel(false);
    }

    drawer.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('data-visible', 'false');
    document.documentElement.classList.remove('spv-scroll-lock');
    removeTrapFocus();
  }

  // ─── Sub-Panel Navigation (iOS-style slide) ───

  /**
   * @param {{ panel: string }} data
   * @param {Event} event
   */
  openSubPanel(data, event) {
    const handle = data.panel;
    const subPanels = this.refs.panelSub ?? [];
    const target = subPanels.find((el) => el.dataset.panel === handle);

    if (!target) return;

    // Update trigger aria
    const trigger = event?.target instanceof Element ? event.target.closest('[aria-expanded]') : null;
    trigger?.setAttribute('aria-expanded', 'true');

    // Slide main panel left, sub-panel in from right
    this.refs.panelMain.setAttribute('data-pushed', 'true');
    target.setAttribute('data-active', 'true');
    this.#activeSubPanel = target;
    this.#activeSubPanelTrigger = trigger;
  }

  closeSubPanel() {
    this.#closeActiveSubPanel(true);
  }

  /**
   * @param {boolean} restoreFocus
   */
  #closeActiveSubPanel(restoreFocus) {
    if (!this.#activeSubPanel) return;

    const trigger = this.#activeSubPanelTrigger;

    this.refs.panelMain.setAttribute('data-pushed', 'false');
    this.#activeSubPanel.setAttribute('data-active', 'false');
    this.#activeSubPanel = null;
    this.#activeSubPanelTrigger = null;

    if (restoreFocus && trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
    }
  }

  // ─── Desktop Mega-Menu ───

  /**
   * @param {Event} event
   */
  activateMega(event) {
    if (this.#megaLeaveTimeout) {
      clearTimeout(this.#megaLeaveTimeout);
      this.#megaLeaveTimeout = null;
    }

    const navItem = event.target instanceof Element ? event.target.closest('.spv-header__nav-item') : null;
    if (!navItem) return;

    const handle = navItem.dataset.menu;
    const megamenus = this.refs.megamenu ?? [];
    const target = megamenus.find((el) => el.dataset.menu === handle);

    // Deactivate current if switching to a different one
    if (this.#activeMegaMenu && this.#activeMegaMenu !== target) {
      this.#deactivateMegaImmediate();
    }

    if (!target) return;

    navItem.querySelector('[aria-expanded]')?.setAttribute('aria-expanded', 'true');
    target.setAttribute('aria-hidden', 'false');
    this.#activeMegaMenu = target;
    this.#activeMegaNavItem = navItem;
  }

  /**
   * @param {Event} event
   */
  deactivateMega(event) {
    if (!this.#activeMegaMenu) return;

    this.#megaLeaveTimeout = setTimeout(() => {
      this.#deactivateMegaImmediate();
    }, 150);
  }

  /**
   * Keep mega-menu open when pointer enters it
   * @param {Event} event
   */
  megamenuEnter(event) {
    if (this.#megaLeaveTimeout) {
      clearTimeout(this.#megaLeaveTimeout);
      this.#megaLeaveTimeout = null;
    }
  }

  /**
   * Close mega-menu when pointer leaves it
   * @param {Event} event
   */
  megamenuLeave(event) {
    this.deactivateMega(event);
  }

  #deactivateMegaImmediate() {
    if (!this.#activeMegaMenu) return;

    const navItem = this.#activeMegaNavItem;
    navItem?.querySelector('[aria-expanded]')?.setAttribute('aria-expanded', 'false');
    this.#activeMegaMenu.setAttribute('aria-hidden', 'true');
    this.#activeMegaMenu = null;
    this.#activeMegaNavItem = null;
  }

  // ─── Keyboard ───

  /** @param {KeyboardEvent} event */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    if (this.#activeMegaMenu) {
      this.#deactivateMegaImmediate();
      return;
    }

    if (this.#activeSubPanel) {
      this.#closeActiveSubPanel(true);
      return;
    }

    const { drawer } = this.refs;
    if (drawer.getAttribute('aria-hidden') === 'false') {
      this.closeDrawer();
    }
  };
}

if (!customElements.get('spv-navigation-component')) {
  customElements.define('spv-navigation-component', SpvNavigation);
}
